import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  type OverCapacityLimit,
  useOverCapacityStore,
} from "@/domains/appointment/appointments/store/use-over-capacity-store";
import { timezoneUtils } from "@/lib/timezone";

interface LimitText {
  title: string;
  lines: string[];
}

function useLimitText() {
  const { t, i18n } = useTranslation("appointments");

  return (limit: OverCapacityLimit): LimitText => {
    const start = timezoneUtils.fromISO(limit.rangeStart).setLocale(i18n.language);
    const end = timezoneUtils.fromISO(limit.rangeEnd).setLocale(i18n.language);
    const times = { start: start.toFormat("HH:mm"), end: end.toFormat("HH:mm") };
    const date = start.toFormat("ccc d MMM");
    const afterConfirm = limit.confirmedCount + 1;

    switch (limit.type) {
      case "TIME_RANGE":
        return {
          title: t("overCapacity.timeRange.title"),
          lines: [
            t("overCapacity.timeRange.allows", { ...times, date, count: limit.limit }),
            t("overCapacity.alreadyConfirmed", { count: limit.confirmedCount }),
            t("overCapacity.timeRange.result", { after: afterConfirm, count: limit.limit }),
          ],
        };
      case "DAY":
        return {
          title: t("overCapacity.day.title"),
          lines: [
            t("overCapacity.day.allows", { date, count: limit.limit }),
            t("overCapacity.alreadyConfirmed", { count: limit.confirmedCount }),
            t("overCapacity.day.result", { after: afterConfirm, count: limit.limit }),
          ],
        };
      case "CONCURRENT":
        return {
          title: t("overCapacity.concurrent.title"),
          lines: [
            t("overCapacity.concurrent.allows", { count: limit.limit }),
            t("overCapacity.concurrent.overlap", { ...times, count: limit.confirmedCount }),
            t("overCapacity.concurrent.result", { count: afterConfirm }),
          ],
        };
    }
  };
}

export function OverCapacityDialog() {
  const { t } = useTranslation("appointments");
  const limits = useOverCapacityStore((state) => state.limits);
  const answer = useOverCapacityStore((state) => state.answer);
  const toText = useLimitText();

  const [first, ...rest] = (limits ?? []).map(toText);

  return (
    <AlertDialog
      open={limits !== null}
      onOpenChange={(open) => {
        if (!open) answer(false);
      }}
    >
      {first && (
        <AlertDialogContent data-testid="over-capacity-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{first.title}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-1">
                {first.lines.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {rest.map((block) => (
            <section key={block.title} className="space-y-1 border-t pt-4 text-sm">
              <h3 className="font-semibold">{block.title}</h3>
              {block.lines.map((line) => (
                <p key={line} className="text-muted-foreground">
                  {line}
                </p>
              ))}
            </section>
          ))}
          <AlertDialogFooter>
            <AlertDialogCancel>{t("overCapacity.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => answer(true)}>
              {t("overCapacity.accept")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      )}
    </AlertDialog>
  );
}
